/**
 * Utilities for building and working with subdivision trees
 */
/**
 * Build a tree structure from a flat list of subdivisions
 */
export function buildSubdivisionTree(subdivisions) {
    const map = {};
    const roots = [];
    // Create map of all subdivisions
    subdivisions.forEach((item) => {
        map[item.id] = {
            ...item,
            children: [],
        };
    });
    // Build tree structure
    subdivisions.forEach((item) => {
        const node = map[item.id];
        if (item.parent_id && map[item.parent_id]) {
            map[item.parent_id].children.push(node);
        }
        else {
            roots.push(node);
        }
    });
    return roots;
}
/**
 * Flatten subdivision tree into HTML options
 */
export function flattenSubdivisionOptions(nodes, level, prefixes, labelKey, language, allowParentSelection, subdivisionNameFilter) {
    let html = '';
    // Apply name filter and get display names
    const nodesWithDisplayNames = nodes
        .map((node) => {
        let displayName = node[labelKey] || node.name;
        const countryCode = node.code ? node.code.split('-')[0] : null;
        // Apply subdivisionNameFilter if provided
        if (subdivisionNameFilter && typeof subdivisionNameFilter === 'function') {
            const filteredName = subdivisionNameFilter(node.code, displayName, language, countryCode, node);
            if (filteredName === false) {
                return null; // Mark for filtering - remove this item
            }
            if (filteredName !== null && filteredName !== undefined) {
                displayName = filteredName;
            }
        }
        return { ...node, _displayName: displayName };
    })
        .filter((node) => node !== null);
    // Sort: use Unicode-aware sorting if subdivisionNameFilter was used
    const locale = subdivisionNameFilter && typeof subdivisionNameFilter === 'function' && language
        ? language
        : undefined;
    nodesWithDisplayNames.sort((a, b) => {
        if (locale) {
            return a._displayName.localeCompare(b._displayName, locale, {
                sensitivity: 'accent', // Case-insensitive, accent-sensitive
                ignorePunctuation: false,
            });
        }
        return a._displayName.localeCompare(b._displayName); // Default behavior
    });
    const prefix = level > 0 ? (prefixes[level] ?? '&nbsp;'.repeat(level * 2)) : '';
    const cssClass = `subdivision-level-${level}`;
    for (const node of nodesWithDisplayNames) {
        const hasChildren = node.children && node.children.length > 0;
        const displayName = node._displayName;
        const label = `${prefix}${displayName}`;
        if (hasChildren) {
            if (allowParentSelection) {
                html += `<option value="${node.code}" class="${cssClass}">${label}</option>`;
            }
            else {
                html += `<option disabled value="${node.code}" class="${cssClass}">${label}</option>`;
            }
            html += flattenSubdivisionOptions(node.children, level + 1, prefixes, labelKey, language, allowParentSelection, subdivisionNameFilter);
        }
        else {
            html += `<option value="${node.code}" class="${cssClass}">${label}</option>`;
        }
    }
    return html;
}
/**
 * Parse nesting prefixes from data attributes
 */
export function parseNestingPrefixes(dataAttributes) {
    const prefixes = {};
    for (let lvl = 1; lvl <= 10; lvl++) {
        const key = `nested${lvl}Prefix`;
        const value = dataAttributes[key];
        if (value !== undefined) {
            prefixes[lvl] = value;
        }
        else {
            break;
        }
    }
    return prefixes;
}
